import { useState, useContext } from "react";
import useAnalytics from "./useAnalytics";
import { isNullOrAll } from "lib/utils/util";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";
import { getLocalDateStringFromString } from "lib/utils/date-utils";
import { deduplicateItemsFromArray } from "lib/utils/array-utils";
import { AppContext } from "lib/contexts/app-context";

interface PatientBookingsState {
  loading: boolean;
  error: string | null;
  filterOptions: PatientBookingFilterOptions;
  appointments: AppointmentBookingDetails[];
  getFilterOptions: () => void;
  getFilterByClinic: (
    clinicId: string,
    onSuccess?: (clinic: ClinicAnalytics) => void
  ) => void;
  getFilterByTherapy: (
    therapyId: string,
    onSuccess?: (therapy: TherapyAnalyticsDetails) => void
  ) => void;
  getFilterByPractitioner: (
    practitionerId: string,
    onSuccess?: (practitioner: PractitionerAnalyticsDetails) => void
  ) => void;
  setFilterOptions: (options: PatientBookingFilterOptions) => void;
  getAppointments: (
    practitionerId: string,
    onSuccess?: (bookings: AppointmentBookingDetails[]) => void
  ) => void;
  bookAppointment: (
    appointmentDetails: AppointmentBookingDetails,
    onSuccess?: (booking: AppointmentBookingDetails) => void
  ) => void;
  updateAppointment: (
    appointmentDetails: AppointmentBookingDetails,
    onSuccess?: (booking: AppointmentBookingDetails) => void
  ) => void;
  bookBusyAppointment: (
    appointmentDetails: AppointmentBookingDetails,
    onSuccess?: (booking: AppointmentBookingDetails) => void
  ) => void;
  getAppointmentDetails: (
    appointmentId: string,
    onSuccess?: (appointment: AppointmentBookingDetails) => void
  ) => void;
  cancelAppointment: (appointmentId: string, onSuccess?: () => void) => void;
  updateAppointmentStatus: (
    appointmentId: string,
    status: string,
    onSuccess?: (booking: AppointmentBookingDetails) => void
  ) => void;
  postFilterPractitionerList: (
    clinicId: string,
    therapyId: string,
    practitionerId: string,
    onSuccess?: (practitioner: PractitionerAnalyticsDetails) => void
  ) => void;
  filterPractitioners: (
    filters: PractitionerFilterRequest,
    onSuccess?: (response: PractitionerFilterResponse) => void
  ) => void;
  markBuyBookingSessionAsPaid: (
    patientId: string,
    transactionId: string,
    onSuccess?: () => void
  ) => void;
  getBookingStateDetails: (
    bookingId: string,
    onSuccess?: (response: BookingState) => void
  ) => void;
}

const usePatientBookings = (): PatientBookingsState => {
  const [appointments, setAppointments] = useState<AppointmentBookingDetails[]>(
    []
  );
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptionsState] = useState<PatientBookingFilterOptions>({
    clinics: [],
    practitioners: [],
    therapies: [],
    selectedClinicId: null,
    selectedPractitionerId: null,
    selectedTherapyId: null,
  });
  const { appState } = useContext(AppContext);

  const {
    getClinics,
    getTherapies,
    getPractitioners,
    getClinicDetails,
    getPractitionerDetails,
    getTherapyDetails,
    error: analyticsError,
    loading: analyticsLoading,
  } = useAnalytics();

  const {
    getData: getAppointmentsRequest,
    loading: getAppointmentsRequestLoading,
  } = useApi();

  const {
    getData: getAppointmentDetailsRequest,
    loading: getAppointmentDetailsRequestLoading,
  } = useApi<AppointmentBookingDetails>();

  const {
    postData: bookAppointmentsRequest,
    loading: bookAppointmentsRequestLoading,
  } = useApi<AppointmentBookingDetails>();

  const {
    postData: updateAppointmentsRequest,
    loading: updateAppointmentsRequestLoading,
  } = useApi<AppointmentBookingDetails>();

  const {
    postData: bookBusyAppointmentsRequest,
    loading: bookBusyAppointmentsRequestLoading,
  } = useApi<AppointmentBookingDetails>();

  const {
    deleteData: cancelAppointmentsRequest,
    loading: cancelAppointmentsRequestLoading,
  } = useApi<AppointmentBookingDetails>();

  /**
   * Update status of booking session after stripe payment is completed
   */
  const {
    getData: markBuyBookingSessionAsPaidRequest,
    loading: markBuyBookingSessionAsPaidRequestLoading,
  } = useApi();

  /**
   * Update appointment status (e.g., mark as paid)
   */
  const { putData: updateAppointmentRequest, loading: updateAppointmentRequestLoading } = useApi<AppointmentBookingDetails>();

  const {
    postData: filterPractitionersRequest,
    loading: filterPractitionersRequestLoading,
  } = useApi<PractitionerFilterResponse>();

  const {
    getData: getBookingStateDetailsRequest,
    loading: getBookingStateDetailsRequestLoading,
  } = useApi<BookingState>();

  const updateAppointmentStatus = async (
    appointmentId: string,
    status: string,
    onSuccess?: (booking: AppointmentBookingDetails) => void
  ) => {
    if (!appState?.accessJWT) {
      setError("Please log in to update appointment status.");
      return;
    }
    setError(null);
    await updateAppointmentRequest(
      `${API_ROUTES.APPOINTMENTS.base}/booking/${appointmentId}`,
      { status },
      onSuccess,
      setError
    );
  };

  const postFilterPractitionerList = async (
    clinicId: string,
    therapyId: string,
    practitionerId: string,
    onSuccess?: (practitioner: PractitionerAnalyticsDetails) => void
  ) => {
    if (!appState?.accessJWT) {
      setError("Please log in to filter practitioners.");
      return;
    }
    setError(null);
    
    // Use the new filter practitioners API
    await filterPractitionersRequest(
      API_ROUTES.ANALYTICS.filterPractitioners,
      { clinicId, therapyId, practitionerId },
      (response) => {
        // Handle both response structures for backward compatibility
        const practitioners = response.data?.practitioners || response.practitioners || [];
        if (practitioners.length > 0) {
          // Convert PractitionerDetails to PractitionerAnalyticsDetails for backward compatibility
          const practitionerDetails = practitioners[0];
          
          // Convert Therapy[] to TherapyAnalytics[]
          const therapiesAnalytics = (response.data?.therapies || []).map(therapy => ({
            id: therapy.id,
            name: therapy.name,
            description: therapy.description,
            expiryDate: therapy.expiryDate,
            prerequisites: [], // Therapy type doesn't have prerequisites
            sessionTime: "0", // Therapy type doesn't have sessionTime
            perSessionPrice: "0.00", // Therapy type doesn't have perSessionPrice
          }));
          
          const practitionerAnalytics: PractitionerAnalyticsDetails = {
            clinics: response.data?.clinics || [],
            therapies: therapiesAnalytics,
            practitioner: practitionerDetails,
          };
          onSuccess?.(practitionerAnalytics);
        }
      },
      setError
    );
  };

  const filterPractitioners = async (
    filters: PractitionerFilterRequest,
    onSuccess?: (response: PractitionerFilterResponse) => void
  ) => {
    if (!appState?.accessJWT) {
      setError("Please log in to access filter practitioners.");
      return;
    }
    setError(null);
    
    try {
      await filterPractitionersRequest(
        API_ROUTES.ANALYTICS.filterPractitioners,
        filters,
        (response) => {
          console.log('Filter practitioners response:', response); // Debug log
          
          // Handle the actual API response structure
          let practitioners = [];
          if (response && response.data && response.data.practitioners) {
            // The practitioners array contains practitioner objects directly
            practitioners = response.data.practitioners.filter(p => p && p.id); // Remove null/invalid items
          } else if (response && response.practitioners) {
            // Fallback to direct response.practitioners if data structure is different
            practitioners = response.practitioners.filter(p => p && p.id);
          }
          
          console.log('Extracted practitioners:', practitioners); // Debug log
          console.log('Practitioners count:', practitioners.length); // Debug log
          
          setFilterOptionsState(prevOptions => ({
            ...prevOptions,
            practitioners,
          }));
          
          onSuccess?.(response);
        },
        (error) => {
          console.error('Filter practitioners error:', error);
          setError(error);
        }
      );
    } catch (error) {
      console.error('Filter practitioners exception:', error);
      setError('Failed to filter practitioners');
    }
  };

  // Get all filter options initially (clinics, therapies, practitioners)
  const getFilterOptions = async () => {
    // Check if user is authenticated before making API calls
    if (!appState?.accessJWT) {
      setError("Please log in to access booking options.");
      return;
    }

    await getClinics((clinics) => {
      setFilterOptionsState(prevOptions => ({ ...prevOptions, clinics }));
      getTherapies((therapies) => {
        setFilterOptionsState(prevOptions => ({ ...prevOptions, therapies }));
        getPractitioners((practitioners) => {
          setFilterOptionsState(prevOptions => ({ ...prevOptions, practitioners }));
        });
      });
    });
  };

  const getFilterByClinic = async (
    clinicId: string,
    onSuccess?: (clinic: ClinicAnalytics) => void
  ) => {
    // Check if user is authenticated before making API calls
    if (!appState?.accessJWT) {
      setError("Please log in to access clinic details.");
      return;
    }

    await getClinicDetails(clinicId, (clinic) => {
      setFilterOptionsState(prevOptions => {
        const newOptions = { ...prevOptions, selectedClinicId: clinicId };
        
        // Filter practitioners based on selected therapy (if any)
        if (isNullOrAll(prevOptions.selectedPractitionerId)) {
          if (!isNullOrAll(prevOptions.selectedTherapyId)) {
            // Filter practitioners from this clinic who offer the selected therapy
            newOptions.practitioners = deduplicateItemsFromArray(
              clinic.practitioners.filter((practitioner) =>
                practitioner.therapies.some(
                  (t) => t.id === prevOptions.selectedTherapyId
                )
              )
            );
          } else {
            // No therapy selected, show all practitioners from clinic
            newOptions.practitioners = deduplicateItemsFromArray(
              clinic.practitioners
            );
          }
        }

        // Filter therapies only if none is selected
        if (isNullOrAll(prevOptions.selectedTherapyId)) {
          newOptions.therapies = deduplicateItemsFromArray(clinic.therapies);
        }

        return newOptions;
      });

      onSuccess?.(clinic);
    });
  };

  const getFilterByPractitioner = async (
    practitionerId: string,
    onSuccess?: (practitioner: PractitionerAnalyticsDetails) => void
  ) => {
    // Check if user is authenticated before making API calls
    if (!appState?.accessJWT) {
      setError("Please log in to access practitioner details.");
      return;
    }

    await getPractitionerDetails(practitionerId, (practitionerDetails) => {
      setFilterOptionsState(prevOptions => {
        const newOptions = { ...prevOptions, selectedPractitionerId: practitionerId };
        newOptions.practitioners = [practitionerDetails.practitioner];
        
        if (isNullOrAll(prevOptions.selectedClinicId)) {
          newOptions.clinics = deduplicateItemsFromArray(
            practitionerDetails.clinics
          );
        }
        if (isNullOrAll(prevOptions.selectedTherapyId)) {
          newOptions.therapies = deduplicateItemsFromArray(
            practitionerDetails.therapies
          );
        }

        return newOptions;
      });

      onSuccess?.(practitionerDetails);
    });
  };

  const getFilterByTherapy = async (
    therapyId: string,
    onSuccess?: (therapy: TherapyAnalyticsDetails) => void
  ) => {
    // Check if user is authenticated before making API calls
    if (!appState?.accessJWT) {
      setError("Please log in to access therapy details.");
      return;
    }

    await getTherapyDetails(therapyId, (therapy) => {
      setFilterOptionsState(prevOptions => {
        const newOptions = { ...prevOptions, selectedTherapyId: therapyId };

        if (isNullOrAll(prevOptions.selectedPractitionerId)) {
          newOptions.practitioners = deduplicateItemsFromArray(
            therapy.practitioners
          );
        }

        if (isNullOrAll(prevOptions.selectedClinicId)) {
          newOptions.clinics = deduplicateItemsFromArray(therapy.clinics);
        }

        return newOptions;
      });

      onSuccess?.(therapy);
    });
  };

  const setFilterOptions = (options: PatientBookingFilterOptions) => {
    setFilterOptionsState(prevOptions => ({
      ...prevOptions,
      selectedClinicId: options.selectedClinicId,
      selectedPractitionerId: options.selectedPractitionerId,
      selectedTherapyId: options.selectedTherapyId,
      // Also update the arrays if provided
      clinics: options.clinics || prevOptions.clinics,
      therapies: options.therapies || prevOptions.therapies,
      practitioners: options.practitioners || prevOptions.practitioners,
    }));
  };

  const getAppointments = async (
    practitionerId: string,
    onSuccess?: (bookings: AppointmentBookingDetails[]) => void
  ) => {
    // Check if user is authenticated before making API calls
    if (!appState?.accessJWT) {
      setError("Please log in to access appointments.");
      return;
    }

    setError(null);
    const endPoint = API_ROUTES.APPOINTMENTS.byPractitioner.replace(
      "{{PRACTITIONER_ID}}",
      practitionerId
    );
    await getAppointmentsRequest(
      endPoint,
      (response: AppointmentBookingDetails[]) => {
        const formattedResponse = response.map((appointment) => ({
          ...appointment,
          fromTime: getLocalDateStringFromString(appointment.fromTime),
        }));

        setAppointments(formattedResponse);
        onSuccess?.(formattedResponse);
      },
      setError
    );
  };

  const bookAppointment = async (
    appointmentDetails: AppointmentBookingDetails,
    onSuccess?: (booking: AppointmentBookingDetails) => void
  ) => {
    // Check if user is authenticated before making API calls
    if (!appState?.accessJWT) {
      setError("Please log in to book appointments.");
      return;
    }

    setError(null);
    await bookAppointmentsRequest(
      `${API_ROUTES.APPOINTMENTS.base}${API_ROUTES.APPOINTMENTS.bookings}`,
      appointmentDetails,
      onSuccess,
      setError
    );
  };

  const updateAppointment = async (
    appointmentDetails: AppointmentBookingDetails,
    onSuccess?: (booking: AppointmentBookingDetails) => void
  ) => {
    // Check if user is authenticated before making API calls
    if (!appState?.accessJWT) {
      setError("Please log in to book appointments.");
      return;
    }

    setError(null);
    await updateAppointmentsRequest(
      `${API_ROUTES.APPOINTMENTS.base}${API_ROUTES.APPOINTMENTS.updateBookings}`,
      appointmentDetails,
      onSuccess,
      setError
    );
  };

  const bookBusyAppointment = async (
    appointmentDetails: AppointmentBookingDetails,
    onSuccess?: (booking: AppointmentBookingDetails) => void
  ) => {
    // Check if user is authenticated before making API calls
    if (!appState?.accessJWT) {
      setError("Please log in to book appointments.");
      return;
    }

    setError(null);
    await bookBusyAppointmentsRequest(
      API_ROUTES.APPOINTMENTS.busyBooking,
      appointmentDetails,
      onSuccess,
      setError
    );
  };

  const getAppointmentDetails = async (
    appointmentId: string,
    onSuccess?: (appointment: AppointmentBookingDetails) => void
  ) => {
    // Check if user is authenticated before making API calls
    if (!appState?.accessJWT) {
      setError("Please log in to access appointment details.");
      return;
    }

    setError(null);
    const endPoint = API_ROUTES.APPOINTMENTS.getBookingDetails.replace(
      "{{BOOKING_ID}}",
      appointmentId
    );
    await getAppointmentDetailsRequest(
      endPoint,
      (response: AppointmentBookingDetails) => {
        const formattedResponse = {
          ...response,
          fromTime: getLocalDateStringFromString(response.fromTime),
        };

        onSuccess?.(formattedResponse);
      },
      setError
    );
  };

  const cancelAppointment = async (
    appointmentId: string,
    onSuccess?: () => void
  ) => {
    // Check if user is authenticated before making API calls
    if (!appState?.accessJWT) {
      setError("Please log in to cancel appointments.");
      return;
    }

    setError(null);
    await cancelAppointmentsRequest(
      API_ROUTES.APPOINTMENTS.cancelAppointment.replace(
        "{{BOOKING_ID}}",
        appointmentId
      ),
      {},
      onSuccess,
      setError
    );
  };

  const markBuyBookingSessionAsPaid = async (
      patientId: string,
      transactionId: string,
      onSuccess?: () => void
    ) => {
      setError(null);
      const endPoint = API_ROUTES.PATIENT.patientBuyBookingSessionSuccess
        .replace("{{PATIENT_ID}}", patientId)
        .replace("{{TRANSACTION_ID}}", transactionId);
      await markBuyBookingSessionAsPaidRequest(
        endPoint,
        () => {
          onSuccess?.();
        },
      setError
    );
  };

  const getBookingStateDetails = async (
    bookingId: string,
    onSuccess?: (booking: BookingState) => void
  ) => {
    setError(null);
    const endPoint = API_ROUTES.APPOINTMENTS.getBookingStateDetails
      .replace("{{BOOKING_ID}}", bookingId);
    await getBookingStateDetailsRequest(
      endPoint,
      (response: BookingState) => {
        onSuccess?.(response);
      },
      setError
    );
  };

  return {
    loading:
      analyticsLoading ||
      getAppointmentsRequestLoading ||
      bookAppointmentsRequestLoading ||
      updateAppointmentsRequestLoading || 
      getAppointmentDetailsRequestLoading ||
      cancelAppointmentsRequestLoading ||
      bookBusyAppointmentsRequestLoading ||
      updateAppointmentRequestLoading ||
      filterPractitionersRequestLoading ||
      markBuyBookingSessionAsPaidRequestLoading ||
      getBookingStateDetailsRequestLoading,
    error: error || analyticsError || analyticsError,
    appointments,
    filterOptions,
    getFilterOptions,
    getFilterByClinic,
    getFilterByPractitioner,
    getFilterByTherapy,
    setFilterOptions,
    getAppointments,
    bookAppointment,
    updateAppointment,
    getAppointmentDetails,
    cancelAppointment,
    bookBusyAppointment,
    updateAppointmentStatus,
    postFilterPractitionerList,
    filterPractitioners,
    markBuyBookingSessionAsPaid,
    getBookingStateDetails,
  };
};

export default usePatientBookings;
